import { Label } from "@/shared/components/ui/label";
import { useQpmMonitorCapsMutation } from "@/services/backend/qpm/monitors/api/monitor-api";
import { Checkbox } from "@/shared/components/ui/checkbox";
import t from "@/lang/lang";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs [&>svg]:bg-background [&>svg]:fill-background";

export function MonitorCapabilitiesComponent({
  monitorId,
  monitorType,
  caps,
  disabled,
  showCheckpointServiceToggle = true,
}: {
  monitorId: string;
  monitorType: MonitorType;
  caps: {
    permanentBaiting: boolean;
    hasLethalAgent: boolean;
    rentable: boolean;
    rentalOnly: boolean;
    checkpointVisual: boolean;
    includeInCheckpointService: boolean;
    iot?: { protocol: string; class: string };
    flyEx?: Record<string, unknown>;
  };
  disabled?: boolean;
  showCheckpointServiceToggle?: boolean;
}) {
  const [update] = useQpmMonitorCapsMutation();
  const isCheckpoint = monitorType === MonitorType.Checkpoint;
  const isFlyExterminator = monitorType === MonitorType.FlyExterminator;
  const isIoT = monitorType === MonitorType.IoT;
  const showQpmServiceOption =
    showCheckpointServiceToggle && (isCheckpoint || isFlyExterminator || isIoT);

  if (!isCheckpoint && !showQpmServiceOption) {
    return null;
  }

  const onChange = (field: keyof typeof caps, value: boolean) => {
    if (disabled) return;
    update({
      id: monitorId,
      permanentBaiting:
        field === "permanentBaiting" ? value : caps.permanentBaiting,
      hasLethalAgent: field === "hasLethalAgent" ? value : caps.hasLethalAgent,
      rentable: field === "rentable" ? value : caps.rentable,
      rentalOnly: field === "rentalOnly" ? value : caps.rentalOnly,
      checkpointVisual:
        field === "checkpointVisual" ? value : caps.checkpointVisual,
      includeInCheckpointService:
        field === "includeInCheckpointService"
          ? value
          : caps.includeInCheckpointService,
      iot: caps.iot,
      flyEx: caps.flyEx,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {isCheckpoint ? (
        <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
          <Checkbox
            className="size-5"
            checked={caps?.hasLethalAgent}
            disabled={disabled}
            onCheckedChange={(v) => onChange("hasLethalAgent", v === true)}
          />
          <Label className="text-sm font-medium">
            {t("Toxischer Wirkstoff")}
          </Label>
        </div>
      ) : null}
      {isCheckpoint ? (
        <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
          <Checkbox
            className="size-5"
            checked={caps?.permanentBaiting}
            disabled={disabled}
            onCheckedChange={(v) => onChange("permanentBaiting", v === true)}
          />
          <Label className="text-sm font-medium">{t("Dauerbeköderung")}</Label>
        </div>
      ) : null}
      {isCheckpoint ? (
        <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
          <Checkbox
            className="size-5"
            checked={caps?.checkpointVisual}
            disabled={disabled}
            onCheckedChange={(v) => onChange("checkpointVisual", v === true)}
          />
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">
              {t("Ausschließlich Visueller Prüfpunkt")}
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={t("Hinweis zum visuellen Prüfpunkt")}
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className={tooltipContentClassName}>
                  <p>
                    {t(
                      "Diese Einstellung ist nur für visuelle Prüfpunkte gedacht. Dadurch können in der Kalkulation eigene Zeiten für diese Monitore gesetzt werden.",
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      ) : null}
      {showQpmServiceOption ? (
        <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
          <Checkbox
            className="size-5"
            checked={caps?.includeInCheckpointService}
            disabled={disabled}
            onCheckedChange={(v) =>
              onChange("includeInCheckpointService", v === true)
            }
          />
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">
              {t("Als QPM Service berechnen")}
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={t("Hinweis zur QPM Service Option")}
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className={tooltipContentClassName}>
                  <p>
                    {t(
                      "Wenn aktiv, wird dieser Monitor in der QPM Wartung berücksichtigt. Wenn deaktiviert, wird er nicht in der QPM Service Berechnung verwendet.",
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      ) : null}
    </div>
  );
}
